import { z } from 'zod'

/**
 * Validates data against a Zod schema and returns the parsed result
 * Throws ZodError if validation fails
 */
export const validateRequest = <T>(schema: z.ZodSchema<T>, data: unknown): T => {
  return schema.parse(data)
}

/**
 * Checks if data is valid against a Zod schema without throwing
 * Returns boolean indicating validity
 */
export const isValidRequest = <T>(schema: z.ZodSchema<T>, data: unknown): boolean => {
  return schema.safeParse(data).success
}

/**
 * Validates data and returns either the parsed result or validation errors
 * Useful for API endpoints that need detailed error information
 */
export const safeValidateRequest = <T>(schema: z.ZodSchema<T>, data: unknown): {
  success: true
  data: T
} | {
  success: false
  errors: z.ZodIssue[]
} => {
  const result = schema.safeParse(data)
  
  if (result.success) {
    return { success: true, data: result.data }
  } else {
    return { success: false, errors: result.error.issues }
  }
}

/**
 * Development helper to log contract validation results
 * Only logs in development environment
 */
export const debugValidation = <T>(
  schema: z.ZodSchema<T>, 
  data: unknown, 
  context: string
): T | null => {
  // Check if we're in development mode (compatible with both Node.js and test environments)
  const isDev = process.env.NODE_ENV === 'development' ||
                process.env.NODE_ENV === 'test'
  
  if (isDev) {
    const result = safeValidateRequest(schema, data)
    
    if (result.success) {
      console.log(`✅ Contract validation passed: ${context}`)
      return result.data
    } else {
      console.warn(`❌ Contract validation failed: ${context}`, {
        errors: result.errors,
        receivedData: data
      })
      return null
    }
  }
  
  // In production, just validate silently
  const result = safeValidateRequest(schema, data)
  return result.success ? result.data : null
}

/**
 * Type guard helper that checks if data matches a schema
 * Useful for TypeScript type narrowing
 */
export const isOfType = <T>(schema: z.ZodSchema<T>, data: unknown): data is T => {
  return schema.safeParse(data).success
}

/**
 * Utility to generate example data from a Zod schema (for testing/documentation)
 * Note: This is a basic implementation - for complex schemas you might want a more sophisticated approach
 */
export const generateExample = <T>(schema: z.ZodSchema<T>, overrides: Partial<T> = {}): Partial<T> => {
  // This is a simplified example generator
  // In a real implementation, you might use a library like @anatine/zod-mock
  const base: Record<string, unknown> = {}
  
  if (schema instanceof z.ZodObject) {
    const shape = schema.shape
    for (const [key, fieldSchema] of Object.entries(shape)) {
      if (fieldSchema instanceof z.ZodString) {
        base[key] = `example_${key}`
      } else if (fieldSchema instanceof z.ZodNumber) {
        base[key] = 123
      } else if (fieldSchema instanceof z.ZodBoolean) {
        base[key] = true
      } else if (fieldSchema instanceof z.ZodOptional) {
        // Skip optional fields in examples
        continue
      }
    }
  }
  
  return { ...base, ...overrides }
} 